import type { TConversationsFilter } from 'src/services/api/messages/use-conversations-search';
import { INITIAL_FILTERS } from 'src/services/api/inboxes/constants';

const INCLUDE_LIST_KEYS = [
    'assigneeIds',
    'ingestionSources',
    'leadStages',
    'leadLabelIds',
    'propertyIds',
    'propertyLabelIds',
] as const;

type IncludeListKey = (typeof INCLUDE_LIST_KEYS)[number];

/**
 * For an include-list dimension, return the inbox's allowed values, used to restrict what the
 * temporary filter picker may offer.
 *
 * @returns The allowed values, or undefined when the inbox does not constrain this dimension
 *          (meaning every value is allowed).
 */
export const getAllowedValuesForKey = (
    inboxFilters: TConversationsFilter | undefined,
    key: IncludeListKey,
): string[] | undefined => {
    const inboxValues = inboxFilters?.[key] as string[] | undefined;
    if (inboxValues !== undefined && inboxValues.length > 0) {
        return inboxValues.map(String);
    }
    return undefined;
};

export const mergeNarrowedFilters = (
    inboxFilters: TConversationsFilter | undefined,
    userFilter: TConversationsFilter,
): TConversationsFilter => {
    if (!inboxFilters) {
        return userFilter;
    }

    const merged: TConversationsFilter = { ...INITIAL_FILTERS, ...inboxFilters };

    for (const key of INCLUDE_LIST_KEYS) {
        const userValues = ((userFilter[key] as string[] | undefined) ?? []).map(String);
        if (userValues.length === 0) {
            // No user pick: keep the inbox's value (or no constraint when the inbox is open).
            continue;
        }
        const inboxValues = (inboxFilters[key] as string[] | undefined) ?? [];
        if (inboxValues.length > 0) {
            // The inbox constrains this dimension; the user can only narrow within it. Intersect
            // so a user pick can never reintroduce values the inbox filtered out.
            const inboxSet = new Set(inboxValues.map(String));
            (merged as Record<string, unknown>)[key] = userValues.filter(v => inboxSet.has(v));
        } else {
            (merged as Record<string, unknown>)[key] = userValues;
        }
    }

    // Excluding more labels only ever narrows the result set, so union the two exclude lists.
    const inboxExcludes = inboxFilters.excludeLeadLabelIds ?? [];
    const userExcludes = userFilter.excludeLeadLabelIds ?? [];
    const mergedExcludes = new Set([...inboxExcludes, ...userExcludes].map(String));
    merged.excludeLeadLabelIds = Array.from(mergedExcludes);

    // Include and exclude must stay disjoint: a label cannot be both "is" and "is not". Excluding
    // narrows, so it wins over including when the inbox and user disagree on the same label.
    merged.leadLabelIds = (merged.leadLabelIds ?? []).filter(id => !mergedExcludes.has(String(id)));

    // Single-value dimensions pinned by the inbox cannot be changed; otherwise the user's value
    // applies on top.
    if (inboxFilters.lastMessageDirection !== undefined) {
        merged.lastMessageDirection = inboxFilters.lastMessageDirection;
    } else if (userFilter.lastMessageDirection !== undefined) {
        merged.lastMessageDirection = userFilter.lastMessageDirection;
    }
    merged.hasNoProperty = Boolean(inboxFilters.hasNoProperty || userFilter.hasNoProperty);

    return merged;
};

/**
 * Count the number of distinct dimensions a filter constrains. Used for the "INBOX FILTER N"
 * count chip.
 */
export const countActiveFilterDimensions = (filter: TConversationsFilter | undefined): number => {
    if (!filter) {
        return 0;
    }
    let count = 0;
    for (const key of [...INCLUDE_LIST_KEYS, 'excludeLeadLabelIds'] as const) {
        if (((filter[key] as string[] | undefined) ?? []).length > 0) {
            count += 1;
        }
    }
    if (filter.lastMessageDirection !== undefined) {
        count += 1;
    }
    if (filter.hasNoProperty) {
        count += 1;
    }
    return count;
};
